import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { isNotNull } from '@/utils/helpers';
import { RootState } from '../../store';
import { deleteAdminQuizV2, postAdminQuizzesV2List, AdminQuizV2ListRequestBody } from '@/features/Quiz/api/getQuizzes';
import { AdminQuizV2ListItem } from '@/features/Quiz/types';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';

const PAGE_SIZE = 20;

export const getAllQuizzesAdminV2 = createAsyncThunk<
    { items: AdminQuizV2ListItem[]; page: number; total: number; totalPages: number },
    {
        activePage: number;
        activeSearchKeyword?: string;
    },
    { rejectValue: string }
>('quizzes/getAllQuizzesAdminV2', async ({ activePage, activeSearchKeyword }) => {
    const body: AdminQuizV2ListRequestBody = {
        page: activePage - 1,
        size: PAGE_SIZE,
        sort: [['id', 'desc']],
        ...(activeSearchKeyword?.trim() ? { title: activeSearchKeyword.trim() } : {}),
    };
    const res = await postAdminQuizzesV2List(body);
    const totalPages = res.size > 0 ? Math.max(1, Math.ceil(res.total / res.size)) : 1;

    return {
        items: res.data ?? [],
        page: activePage,
        total: res.total,
        totalPages,
    };
});

export const removeQuizAdminV2 = createAsyncThunk(
    'quizzes/removeQuizAdminV2',
    async ({ quizUuid, id }: { quizUuid: string; id: number }) => {
        await deleteAdminQuizV2(quizUuid);
        return id;
    }
);

const quizAdminV2Adapter = createEntityAdapter<AdminQuizV2ListItem>();

type State = {
    pages: Record<number, AdminQuizV2ListItem['id'][]>;
    activePage: number;
    total: number;
    totalPages: number;
    isLoading: boolean;
};

const quizAdminV2Slice = createSlice({
    name: 'quizAdminV2',
    initialState: quizAdminV2Adapter.getInitialState<State>({
        pages: {},
        activePage: 1,
        totalPages: 1,
        total: 0,
        isLoading: false,
    }),
    reducers: {
        setQuizAdminV2ActivePage: (state, action: PayloadAction<number>) => {
            state.activePage = action.payload;
        },
        resetQuizAdminV2Pages: (state) => {
            state.pages = {};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getAllQuizzesAdminV2.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(getAllQuizzesAdminV2.fulfilled, (state, action) => {
            quizAdminV2Adapter.upsertMany(state, action.payload.items);
            state.pages[action.payload.page] = action.payload.items.map((item) => item.id);
            state.total = action.payload.total;
            state.activePage = action.payload.page;
            state.totalPages = action.payload.totalPages;
            state.isLoading = false;
        });
        builder.addCase(getAllQuizzesAdminV2.rejected, (state) => {
            state.isLoading = false;
        });
        builder.addCase(removeQuizAdminV2.fulfilled, (state, action) => {
            quizAdminV2Adapter.removeOne(state, action.payload);
        });
    },
});

export const quizAdminV2 = quizAdminV2Slice.reducer;
export const { resetQuizAdminV2Pages, setQuizAdminV2ActivePage } = quizAdminV2Slice.actions;

const selectState = (state: RootState) => state.quizzes.quizAdminV2;

export const { selectById: selectQuizAdminV2ById } = quizAdminV2Adapter.getSelectors(selectState);

const selectRootState = (state: RootState) => state;

const selectIdsByPage = createSelector(
    [selectState, (_: RootState, page: number) => page],
    (state, page) => state.pages[page] ?? []
);

export const selectQuizAdminV2Page = createSelector(
    [selectRootState, (state: RootState, page: number) => selectIdsByPage(state, page)],
    (state, ids) => ids.map((id) => selectQuizAdminV2ById(state, id)).filter(isNotNull)
);

export const selectQuizAdminV2ActivePage = createSelector([selectState], (state) => state.activePage);
export const selectQuizAdminV2TotalPages = createSelector([selectState], (state) => state.totalPages);
export const selectQuizAdminV2Loading = createSelector([selectState], (state) => state.isLoading);
